import type { ModuleStyle } from '../domain/types';
import {
  circle,
  cross,
  flower,
  heart,
  leaf,
  lightning,
  polygon,
  positionalNoise,
  rect,
  regularPolygon,
  roundedRect,
  starPolygon,
  wave,
} from './geometry';

/**
 * Path data for a single dark module.
 *
 * `row`/`col` are only consumed by position-dependent styles (currently
 * `bubbles`), but every style receives them so the signature stays uniform.
 */
export function modulePath(
  style: ModuleStyle,
  x: number,
  y: number,
  size: number,
  row: number,
  col: number,
): string {
  const cx = x + size / 2;
  const cy = y + size / 2;

  switch (style) {
    case 'classic':
      return rect(x, y, size, size);

    case 'dotted':
      return circle(cx, cy, size * 0.2);

    case 'circle':
      return circle(cx, cy, size / 2);

    case 'rounded':
      return roundedRect(x, y, size, size, size / 4);

    case 'rounded_rect':
      return roundedRect(x, y, size, size, size / 8);

    case 'bubbles': {
      // 50%–100% of the module, stable for a given grid position.
      const scale = 0.5 + positionalNoise(row, col) * 0.5;
      return circle(cx, cy, (size * 0.8 * scale) / 2);
    }

    case 'diamond':
      return polygon([
        [cx, y],
        [x + size, cy],
        [cx, y + size],
        [x, cy],
      ]);

    case 'classy': {
      const margin = size * 0.2;
      return rect(x + margin, y + margin, size - margin * 2, size - margin * 2);
    }

    case 'classy_rounded': {
      const margin = size * 0.2;
      const inner = size - margin * 2;
      return roundedRect(x + margin, y + margin, inner, inner, inner / 4);
    }

    case 'hexagon':
      return regularPolygon(cx, cy, size * 0.45, 6);

    case 'star':
      return starPolygon(cx, cy, size / 2, size / 4, 5);

    case 'heart':
      return heart(cx, cy, size * 0.9);

    case 'leaf':
      return leaf(cx, cy, size * 0.85);

    case 'triangle':
      return polygon([
        [cx, y + size * 0.1],
        [x + size * 0.92, y + size * 0.88],
        [x + size * 0.08, y + size * 0.88],
      ]);

    case 'cross':
      return cross(cx, cy, size * 0.85, size * 0.32);

    case 'flower':
      return flower(cx, cy, size / 2, 6);

    case 'lightning':
      return lightning(cx, cy, size * 0.95);

    case 'waves':
      return wave(cx, cy, size * 0.36, 4, size * 0.1);
  }
}
